import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '~/lib/cn';

type Country = {
  name: string;
  currencies: string;
  /** Circular flag SVG under /public/images/flags (from the circle-flags set). */
  flag: string;
};

/**
 * Country / currency list shown in the Transfer card (Figma reference).
 * Order: (1) featured top entries, (2) other local-currency payouts,
 * (3) remaining Veem USD-only corridors. Europe/SEPA stays a single row.
 */
const countries: Country[] = [
  // 1 — Featured (keep currencies as-is)
  { name: 'Egypt', currencies: 'EGP, USD, EUR, GBP', flag: '/images/flags/eg.svg' },
  { name: 'Pakistan', currencies: 'PKR, USD, EUR, GBP', flag: '/images/flags/pk.svg' },
  { name: 'Bangladesh', currencies: 'BDT, USD, EUR, GBP', flag: '/images/flags/bd.svg' },
  { name: 'United States', currencies: 'USD', flag: '/images/flags/us.svg' },
  { name: 'Europe', currencies: 'USD, EUR', flag: '/images/flags/eu.svg' },

  // 2 — Remaining local-currency payouts
  { name: 'Turkey', currencies: 'TRY, USD', flag: '/images/flags/tr.svg' },
  { name: 'Morocco', currencies: 'MAD, USD', flag: '/images/flags/ma.svg' },
  { name: 'United Arab Emirates', currencies: 'AED, USD', flag: '/images/flags/ae.svg' },
  { name: 'Qatar', currencies: 'QAR, USD', flag: '/images/flags/qa.svg' },
  { name: 'Saudi Arabia', currencies: 'SAR, USD', flag: '/images/flags/sa.svg' },
  { name: 'Nigeria', currencies: 'NGN, USD', flag: '/images/flags/ng.svg' },
  { name: 'Syria', currencies: 'SYP, USD', flag: '/images/flags/sy.svg' },
  { name: 'United Kingdom', currencies: 'GBP, USD', flag: '/images/flags/gb.svg' },

  // 3 — Veem USD-only (skip duplicates from sections 1–2 and Europe/SEPA)
  { name: 'Albania', currencies: 'USD', flag: '/images/flags/al.svg' },
  { name: 'Armenia', currencies: 'USD', flag: '/images/flags/am.svg' },
  { name: 'Argentina', currencies: 'USD', flag: '/images/flags/ar.svg' },
  { name: 'Barbados', currencies: 'USD', flag: '/images/flags/bb.svg' },
  { name: 'Bahrain', currencies: 'USD', flag: '/images/flags/bh.svg' },
  { name: 'Bermuda', currencies: 'USD', flag: '/images/flags/bm.svg' },
  { name: 'Brunei', currencies: 'USD', flag: '/images/flags/bn.svg' },
  { name: 'Brazil', currencies: 'USD', flag: '/images/flags/br.svg' },
  { name: 'Bahamas', currencies: 'USD', flag: '/images/flags/bs.svg' },
  { name: 'Canada', currencies: 'USD', flag: '/images/flags/ca.svg' },
  { name: 'Switzerland', currencies: 'USD', flag: '/images/flags/ch.svg' },
  { name: 'Chile', currencies: 'USD', flag: '/images/flags/cl.svg' },
  { name: 'China', currencies: 'USD', flag: '/images/flags/cn.svg' },
  { name: 'Colombia', currencies: 'USD', flag: '/images/flags/co.svg' },
  { name: 'Costa Rica', currencies: 'USD', flag: '/images/flags/cr.svg' },
  { name: 'Algeria', currencies: 'USD', flag: '/images/flags/dz.svg' },
  { name: 'Ecuador', currencies: 'USD', flag: '/images/flags/ec.svg' },
  { name: 'Fiji', currencies: 'USD', flag: '/images/flags/fj.svg' },
  { name: 'Georgia', currencies: 'USD', flag: '/images/flags/ge.svg' },
  { name: 'Guatemala', currencies: 'USD', flag: '/images/flags/gt.svg' },
  { name: 'Hong Kong', currencies: 'USD', flag: '/images/flags/hk.svg' },
  { name: 'Honduras', currencies: 'USD', flag: '/images/flags/hn.svg' },
  { name: 'India', currencies: 'USD', flag: '/images/flags/in.svg' },
  { name: 'Israel', currencies: 'USD', flag: '/images/flags/il.svg' },
  { name: 'Jamaica', currencies: 'USD', flag: '/images/flags/jm.svg' },
  { name: 'Jordan', currencies: 'USD', flag: '/images/flags/jo.svg' },
  { name: 'Japan', currencies: 'USD', flag: '/images/flags/jp.svg' },
  { name: 'Kenya', currencies: 'USD', flag: '/images/flags/ke.svg' },
  { name: 'South Korea', currencies: 'USD', flag: '/images/flags/kr.svg' },
  { name: 'Kuwait', currencies: 'USD', flag: '/images/flags/kw.svg' },
  { name: 'Cayman Islands', currencies: 'USD', flag: '/images/flags/ky.svg' },
  { name: 'Kazakhstan', currencies: 'USD', flag: '/images/flags/kz.svg' },
  { name: 'Lebanon', currencies: 'USD', flag: '/images/flags/lb.svg' },
  { name: 'Macau', currencies: 'USD', flag: '/images/flags/mo.svg' },
  { name: 'Mauritius', currencies: 'USD', flag: '/images/flags/mu.svg' },
  { name: 'Malawi', currencies: 'USD', flag: '/images/flags/mw.svg' },
  { name: 'Malaysia', currencies: 'USD', flag: '/images/flags/my.svg' },
  { name: 'Mozambique', currencies: 'USD', flag: '/images/flags/mz.svg' },
  { name: 'Nicaragua', currencies: 'USD', flag: '/images/flags/ni.svg' },
  { name: 'Nepal', currencies: 'USD', flag: '/images/flags/np.svg' },
  { name: 'New Zealand', currencies: 'USD', flag: '/images/flags/nz.svg' },
  { name: 'Oman', currencies: 'USD', flag: '/images/flags/om.svg' },
  { name: 'Peru', currencies: 'USD', flag: '/images/flags/pe.svg' },
  { name: 'Philippines', currencies: 'USD', flag: '/images/flags/ph.svg' },
  { name: 'Serbia', currencies: 'USD', flag: '/images/flags/rs.svg' },
  { name: 'Eswatini', currencies: 'USD', flag: '/images/flags/sz.svg' },
  { name: 'Thailand', currencies: 'USD', flag: '/images/flags/th.svg' },
  { name: 'Tunisia', currencies: 'USD', flag: '/images/flags/tn.svg' },
  { name: 'Taiwan', currencies: 'USD', flag: '/images/flags/tw.svg' },
  { name: 'Ukraine', currencies: 'USD', flag: '/images/flags/ua.svg' },
  { name: 'Uganda', currencies: 'USD', flag: '/images/flags/ug.svg' },
  { name: 'South Africa', currencies: 'USD', flag: '/images/flags/za.svg' },
  { name: 'Zambia', currencies: 'USD', flag: '/images/flags/zm.svg' },
];

/**
 * Floating country / currency selector, overlaid on the Transfer feature image.
 *
 * White rounded card with a vertical list of countries (circular flag + name +
 * currency list). A single fully-rounded grey highlight slides/resizes to track
 * the hovered row (same technique as the hero balances card), resting on the
 * currently selected row (Egypt by default) when the cursor leaves. Clicking a
 * row selects it. The list is clipped by the card's bottom edge so the last
 * visible row is partially cut off, implying more items.
 */
export function CountrySelectorCard({ className }: { className?: string }) {
  const [selected, setSelected] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);
  const [reduced, setReduced] = useState(false);
  const [pill, setPill] = useState<{ top: number; height: number } | null>(null);

  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const active = hovered ?? selected;

  useLayoutEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  // Measure the active row (offsets are relative to the positioned <ul>) and
  // move the single highlight to sit behind it.
  useLayoutEffect(() => {
    const el = rowRefs.current[active];
    if (!el) return;
    setPill({ top: el.offsetTop, height: el.offsetHeight });
  }, [active]);

  return (
    <div
      className={cn(
        // Height fits p-5 (40px) + scroll viewport for 3.5 rows (see .country-selector-list).
        'pointer-events-auto flex w-[314px] max-w-[82%] flex-col overflow-hidden rounded-[24px] bg-white p-5 shadow-pill',
        // On mobile the square image is small: use a shorter layout box so the overlay grid can
        // truly centre it, plus a proportional scale-down for clear top/bottom breathing space.
        'origin-center scale-[0.82] lg:scale-100',
        className,
      )}>
      <ul
        className="country-selector-list relative flex flex-col gap-5 overflow-y-auto overscroll-y-contain"
        onMouseLeave={() => setHovered(null)}>
        {/* Single fully-rounded highlight that glides/resizes to the active row. */}
        {pill && (
          <div
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-x-0 top-0 rounded-full bg-black-6',
              !reduced && 'transition-[transform,height] duration-200 ease-nav',
            )}
            style={{ transform: `translateY(${pill.top}px)`, height: pill.height }}
          />
        )}

        {countries.map((country, i) => (
          <li
            key={country.name}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            className="relative z-10">
            <button
              type="button"
              aria-pressed={selected === i}
              onClick={() => setSelected(i)}
              onMouseEnter={() => setHovered(i)}
              onFocus={() => setHovered(i)}
              onBlur={() => setHovered(null)}
              className="flex w-full items-center gap-4 rounded-full px-4 py-[10px] text-left">
              <img
                src={country.flag}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                className="size-8 shrink-0 rounded-full object-cover"
              />
              <span className="flex min-w-0 flex-col gap-[10px]">
                <span className="truncate text-body font-semibold text-ink [text-box-trim:none]">
                  {country.name}
                </span>
                <span className="truncate text-body font-medium text-grey-900 [text-box-trim:none]">
                  {country.currencies}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
